import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, ArrowRight, ArrowLeft, Clock, CalendarCheck,
  UserPlus, CalendarClock, Building2, Camera, Rocket,
  MapPin, ShieldCheck, BadgeCheck, PhoneOff, SlidersHorizontal,
  CheckCircle2, Send, Smartphone, CalendarDays, User, Users, Phone, PawPrint, ClipboardList,
  MessageSquare, Tag, Zap, BarChart3,
} from "lucide-react";
import { useLang } from "@/lib/i18n";
import { Footer } from "@/components/Footer";
import { BRAND } from "@/lib/api";
import horizontalLogo from "@/assets/brand/logo-tagdada-horizontal.png";
import viaApp from "@/assets/postad/via-app.jpg";
import quality from "@/assets/postad/quality.jpg";
import PostAdHero from "@/components/postad/PostAdHero";
import { AgentLinkSection, TenantScreeningSection } from "@/components/postad/AgentSections";

const IMG = {
  viaApp,
  quality,
  logo: horizontalLogo,
};

const C = {
  ru: {
    back: "К объявлениям",
    nav_open: "Открыть аккаунт",
    badge: "Разместите объявление бесплатно",
    hero_pill: "Лучшее приложение календаря и контактов",
    free_line: "Все функции бесплатны — навсегда",
    credit_reg: "бесплатных кредитов при регистрации",
    credit_daily: "Ежедневные бесплатные кредиты",
    h1a: "Календарь и контакты",
    h1b: "для показов недвижимости",
    hero_sub:
      "Создайте полную карточку объекта, снимите проверенные фото в приложении и получайте онлайн-заявки на показ прямо в календарь и Telegram. Аренда, посуточно и продажа — всё в одном месте.",
    cta_open: "Открыть аккаунт бесплатно",
    cta_browse: "Смотреть объявления",
    credits_num: "400",
    credits_label: "бесплатных кредитов при регистрации",
    credits_daily: "+ ежедневные бонусные кредиты",
    // Steps
    steps_title: "Разместите объявление за 5 шагов",
    steps: [
      { t: "Откройте аккаунт", d: "Регистрация бесплатна — сразу 400 кредитов и ежедневные бонусы." },
      { t: "Подключите календарь", d: "Синхронизация с Яндекс и Google Календарём в пару кликов." },
      { t: "Создайте карточку объекта", d: "Аренда, посуточно или продажа — фото, характеристики, условия и цена." },
      { t: "Добавьте фото", d: "Минимум одно проверенное фото на комнату — через приложение. Дополнительно загрузите обработанные кадры для лучшего качества." },
      { t: "Опубликуйте — бесплатно", d: "И получайте онлайн-заявки на показ в календарь и Telegram." },
    ],
    // Verified photos
    vf_kicker: "Проверенные фото",
    vf_title: "Гарантия, что объект — реальный",
    vf_lead:
      "Наша цель — гарантировать пользователям, что в объявлениях показаны реальные объекты. Для каждой комнаты, к которой вы добавляете фото, как минимум одно должно быть снято через приложение — с меткой времени и геолокацией; совпадение с адресом объекта проверяется автоматически. Несовпадение GPS-данных не блокирует публикацию, а обработанные фото можно добавлять дополнительно.",
    vf_badges: { date: "Дата", time: "Время", loc: "Локация", auth: "Подлинное" },
    vf_col1_tag: "Обязательно",
    vf_col1_title: "Снимок через приложение",
    vf_col1_sub: "Минимум одно фото каждой комнаты — снято прямо в приложении, без загрузок.",
    vf_col1_points: [
      "Метка времени и геолокация на каждом кадре",
      "Автосопоставление с адресом объекта",
      "Несовпадение GPS не блокирует публикацию",
    ],
    vf_col2_tag: "Дополнительно",
    vf_col2_title: "Фото для лучшего качества",
    vf_col2_sub: "Добавьте обработанные кадры, чтобы показать объект в лучшем виде.",
    vf_col2_points: [
      "Загружайте красивые отредактированные фото",
      "Столько снимков, сколько нужно",
      "Подлинность уже подтверждена проверочным фото",
    ],
    // Booking
    bk_kicker: "Онлайн-запись на показы",
    bk_title: "Запись на показ без единого звонка",
    bk_lead:
      "Эпоха неверных номеров, «не дозвониться» и бесконечной переписки ради удобного времени показа для вас и клиента — позади. Расписание доступности задаётся отдельно для каждого объекта.",
    bk_points: [
      { t: "Расписание для каждого объекта", d: "Своя доступность на каждый объект, синхронизация с Яндекс и Google Календарём." },
      { t: "Буферы и лимиты показов", d: "tagdada.com добавляет буферы между показами и ограничивает число показов в день." },
      { t: "Запись онлайн без звонков", d: "Гость выбирает свободный слот сам — без звонков и SMS." },
      { t: "Анкета заявителя", d: "Заранее получите имя, контакты, число людей, питомцев и желаемую дату заезда." },
    ],
    bk_schedule: "Расписание объекта",
    bk_hint: "Гость выбирает только свободное время",
    bk_form_title: "Анкета заявителя",
    bk_form_fields: ["Имя", "Контакты", "Количество человек", "Питомцы", "Желаемая дата заезда"],
    // Telegram
    tg_kicker: "Мы на связи в Telegram",
    tg_title: "Все заявки на показ — прямо в Telegram",
    tg_sub:
      "Получайте каждую заявку на показ мгновенно через нашего Telegram-бота. Просто подключитесь на tagdada.com — и ничего не пропустите.",
    tg_cta: "Подключить Telegram",
    cta_account_now: "Открыть бесплатный аккаунт сейчас",
    // Learn more
    learn_kicker: "Больше о Tagdada",
    learn_title: "— это ещё и мощные инструменты",
    learn_sub: "Используйте Tagdada для всех событий и контактов в вашей жизни — не только для операций с недвижимостью.",
    cm_title_a: "Отличный инструмент",
    cm_title_b: "управления контактами",
    cm_sub: "Отмечайте людей тегами, оставляйте короткие заметки, ставьте напоминания связаться, добавляйте их в списки для событий и держите все контактные данные под рукой.",
    cm_features: [
      { t: "Быстрые заметки", d: "Оставьте заметку на любом контакте — в следующий раз контекст будет в одно касание." },
      { t: "Теги и сегменты", d: "Отмечайте людей по типу, статусу или приоритету и фильтруйте за секунды." },
      { t: "Напоминания «связаться»", d: "Поставьте напоминание связаться — Tagdada подскажет, когда придёт время." },
      { t: "Импорт в пару кликов", d: "Импортируйте контакты со смартфона или компьютера в пару кликов — iPhone, Android, Google, Outlook или файл." },
    ],
    mt_title_a: "4 способа назначить встречу.",
    mt_title_b: "Всегда одна ссылка.",
    mt_cards: [
      { t: "Быстрая встреча", d: "Идеально для разовой встречи 1-на-1. Предложите пару дат — гость выберет удобную, и оба календаря обновятся автоматически." },
      { t: "Доступность", d: "Поделитесь своими свободными слотами. Гости выбирают удобное время — без переписки туда-сюда." },
      { t: "Голосование", d: "Пусть все проголосуют за лучшее время. Самый популярный слот побеждает автоматически." },
      { t: "Очередь", d: "Собирайте заявки на встречи и авто-назначайте слоты. Кто первый — тот и записан." },
    ],
    mt_card_cta: ["Запланировать", "Задать доступность", "Начать голосование", "Открыть очередь"],
    // Final
    final_title: "Откройте Tagdada и создайте аккаунт прямо сейчас",
    final_sub: "Это бесплатно, 400 кредитов уже ждут вас.",
    final_cta: "Открыть аккаунт",
  },
  en: {
    back: "Back to listings",
    nav_open: "Open account",
    badge: "Post your listing for free",
    hero_pill: "The best calendar & contacts app",
    free_line: "Every feature is free — always",
    credit_reg: "free credits on sign-up",
    credit_daily: "Daily free credits to earn",
    h1a: "The calendar & contacts",
    h1b: "for property viewings",
    hero_sub:
      "Build a full property card, take verified photos inside the app, and receive online viewing requests straight to your calendar and Telegram. Rent, short-term and sale — all in one place.",
    cta_open: "Open a free account",
    cta_browse: "Browse listings",
    credits_num: "400",
    credits_label: "free credits on sign-up",
    credits_daily: "+ daily free credit opportunities",
    steps_title: "Publish your listing in 5 steps",
    steps: [
      { t: "Open account", d: "Sign-up is free — 400 credits instantly plus daily bonuses." },
      { t: "Connect your calendar", d: "Sync with Yandex and Google Calendar in a couple of clicks." },
      { t: "Create property card", d: "Rent, short-term or sale — photos, specs, terms and price." },
      { t: "Add pictures", d: "At least one verified in-app photo per room. Optionally upload edited pictures to improve the imagery." },
      { t: "Publish — free", d: "And get online viewing requests in your calendar and Telegram." },
    ],
    vf_kicker: "Verified photos",
    vf_title: "Proof the property is real",
    vf_lead:
      "Our goal is to guarantee to users that listings show real properties. For each room you add photos to, at least one must be taken via the app — timestamped and geo-located; the match with the property address is checked automatically. A GPS mismatch does not block publication, and edited photos can be added on top.",
    vf_badges: { date: "Date", time: "Time", loc: "Location", auth: "Authentic" },
    vf_col1_tag: "Required",
    vf_col1_title: "Shot via the app",
    vf_col1_sub: "At least one photo of each room — taken right in the app, no uploads.",
    vf_col1_points: [
      "Timestamp & geolocation on every shot",
      "Auto-matched to the property address",
      "A GPS mismatch won't block publication",
    ],
    vf_col2_tag: "Optional",
    vf_col2_title: "Photos for best quality",
    vf_col2_sub: "Add edited shots to present the property at its best.",
    vf_col2_points: [
      "Upload beautiful, edited photos",
      "As many shots as you need",
      "Authenticity already proven by the in-app photo",
    ],
    bk_kicker: "Online viewing booking",
    bk_title: "Book a viewing without a single call",
    bk_lead:
      "The era of wrong numbers, 'can't connect', and endless messaging just to book a convenient viewing time for you and the client is over. The availability schedule is set per property.",
    bk_points: [
      { t: "A schedule per property", d: "Each property gets its own availability, synced with Yandex and Google Calendar." },
      { t: "Buffers & daily limits", d: "tagdada.com adds buffers between viewings and caps how many happen per day." },
      { t: "Online booking, no calls", d: "Guests pick a free slot themselves — no calls, no SMS." },
      { t: "Requestor questionnaire", d: "Get the name, contacts, number of people, pets and earliest move-in date upfront." },
    ],
    bk_schedule: "Property schedule",
    bk_hint: "Guests can only pick free time",
    bk_form_title: "Requestor questionnaire",
    bk_form_fields: ["Name", "Contacts", "Number of people", "Pets", "Earliest move-in date"],
    tg_kicker: "We're connected to Telegram",
    tg_title: "All viewing requests — straight to Telegram",
    tg_sub:
      "Get every viewing request instantly through our Telegram bot. Just connect at tagdada.com and never miss a thing.",
    tg_cta: "Connect Telegram",
    cta_account_now: "Open a free account now",
    learn_kicker: "More about Tagdada",
    learn_title: "is powerful tools, too",
    learn_sub: "You use Tagdada for all events and contacts in your life, not just for real estate operations.",
    cm_title_a: "Amazing",
    cm_title_b: "contacts management tool",
    cm_sub: "Tag people, make small notes, set reminders to contact them, add them to lists for events, and keep track of all their contact details.",
    cm_features: [
      { t: "Quick comments", d: "Jot a note on any contact — the context is one tap away next time." },
      { t: "Tags & segments", d: "Tag people by type, status or priority and filter in seconds." },
      { t: "'Plan soon' reminders", d: "Set a reminder to reach out — Tagdada nudges you when it's time." },
      { t: "Import in a few clicks", d: "Bring your contacts in from your smartphone or computer in a few clicks — iPhone, Android, Google, Outlook or a file." },
    ],
    mt_title_a: "4 ways to schedule meetings.",
    mt_title_b: "Always one link.",
    mt_cards: [
      { t: "Quick meeting", d: "Perfect for a one-time 1-to-1 meeting. Offer a few dates, your guest picks the one that works, and both calendars update automatically." },
      { t: "Availability", d: "Share your open time slots. Invitees pick what works — no back-and-forth emails." },
      { t: "Polling", d: "Let everyone vote on the best time. The most popular slot wins automatically." },
      { t: "Queue", d: "Collect meeting requests, auto-assign time slots. First in — first scheduled." },
    ],
    mt_card_cta: ["Plan now", "Set availabilities", "Start a vote", "Open a queue"],
    final_title: "Discover Tagdada and open an account now",
    final_sub: "It's free — 400 credits are already waiting for you.",
    final_cta: "Open account",
  },
};

const STEP_META = [
  { Icon: UserPlus, ring: "from-[#F26B2A] to-[#F59B42]", chip: "bg-[#FFF1E8] text-[#F26B2A]" },
  { Icon: CalendarClock, ring: "from-[#7C3AED] to-[#A855F7]", chip: "bg-[#F3E8FF] text-[#7C3AED]" },
  { Icon: Building2, ring: "from-[#0EA5E9] to-[#38BDF8]", chip: "bg-[#E0F2FE] text-[#0284C7]" },
  { Icon: Camera, ring: "from-[#059669] to-[#34D399]", chip: "bg-[#DCFCE7] text-[#059669]" },
  { Icon: Rocket, ring: "from-[#DB2777] to-[#F472B6]", chip: "bg-[#FCE7F3] text-[#DB2777]" },
];

const BADGE_META = [
  { key: "date", Icon: CalendarDays, cls: "bg-[#F26B2A]" },
  { key: "time", Icon: Clock, cls: "bg-[#7C3AED]" },
  { key: "loc", Icon: MapPin, cls: "bg-emerald-500" },
  { key: "auth", Icon: ShieldCheck, cls: "bg-slate-900" },
];

const BK_ICONS = [CalendarClock, SlidersHorizontal, PhoneOff, ClipboardList];
const FORM_ICONS = [User, Phone, Users, PawPrint, CalendarClock];
const CM_ICONS = [MessageSquare, Tag, Clock, UserPlus];
const MT_META = [
  { Icon: Zap, bar: "bg-[#F26B2A]", tile: "bg-[#FFF1E8] text-[#F26B2A]", btn: "bg-[#F26B2A] hover:bg-[#E05A19]" },
  { Icon: CalendarCheck, bar: "bg-emerald-500", tile: "bg-emerald-50 text-emerald-600", btn: "bg-emerald-500 hover:bg-emerald-600" },
  { Icon: BarChart3, bar: "bg-[#F5B518]", tile: "bg-amber-50 text-amber-600", btn: "bg-[#F5B518] hover:bg-[#E0A50E]" },
  { Icon: Users, bar: "bg-[#7C3AED]", tile: "bg-violet-50 text-[#7C3AED]", btn: "bg-[#7C3AED] hover:bg-[#6D28D9]" },
];

export default function PostAd() {
  const { lang, setLang } = useLang();
  const c = C[lang] || C.ru;

  const AccountCTA = ({ className, testid }) => (
    <a href={BRAND.register} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-xl font-bold px-6 py-3.5 shadow-md transition-all active:scale-[0.98] ${className}`}>
      <Sparkles className="h-5 w-5" /> {c.cta_account_now} <ArrowRight className="h-5 w-5" />
    </a>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden" data-testid="post-ad-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[68px] flex items-center gap-4">
          <Link to="/" data-testid="postad-logo" className="flex items-center shrink-0">
            <img src={BRAND.logo} alt="Tagdada" className="h-11 sm:h-14 w-auto" />
          </Link>
          <Link to="/" data-testid="postad-back" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#F26B2A] transition-colors">
            <ArrowLeft className="h-4 w-4" /> {c.back}
          </Link>
          <div className="flex items-center gap-3 ml-auto shrink-0">
            <div className="flex items-center rounded-full border border-slate-200 bg-slate-50 p-0.5 text-xs font-bold">
              {["ru", "en"].map((l) => (
                <button key={l} data-testid={`postad-lang-${l}`} onClick={() => setLang(l)}
                  className={`px-2.5 sm:px-3 py-1.5 rounded-full transition-all ${lang === l ? "bg-white text-slate-900 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <a href={BRAND.register} target="_blank" rel="noopener noreferrer" data-testid="postad-header-cta"
              className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#F26B2A] to-[#F59B42] hover:from-[#E05A19] hover:to-[#E48A31] text-white text-sm font-bold px-4 py-2.5 shadow-sm transition-all active:scale-[0.98]">
              <Sparkles className="h-4 w-4" /> {c.nav_open}
            </a>
          </div>
        </div>
      </header>

      <PostAdHero lang={lang} />

      {/* Steps — bright band */}
      <section className="bg-gradient-to-br from-[#FFF6F0] via-white to-[#F3E8FF] border-y border-slate-100" data-testid="postad-steps-section">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 text-center">{c.steps_title}</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {c.steps.map((s, i) => {
              const m = STEP_META[i];
              const Icon = m.Icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.07 }} data-testid={`postad-step-${i}`}
                  className="relative rounded-2xl bg-white border border-slate-200 p-6 shadow-sm hover:shadow-lg transition-all">
                  <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${m.ring} flex items-center justify-center shadow-md`}>
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <span className={`mt-4 inline-flex items-center justify-center h-7 w-7 rounded-full text-sm font-extrabold ${m.chip}`}>{i + 1}</span>
                  <h4 className="mt-3 text-lg font-extrabold text-slate-900 leading-snug">{s.t}</h4>
                  <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{s.d}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-12 flex justify-center">
            <AccountCTA testid="postad-steps-cta" className="bg-gradient-to-r from-[#F26B2A] to-[#F59B42] hover:from-[#E05A19] hover:to-[#E48A31] text-white" />
          </div>
        </div>
      </section>

      {/* Verified photos — dark section */}
      <section className="relative overflow-hidden bg-[#0B1220] text-white" data-testid="postad-verified-section">
        <div className="pointer-events-none absolute -top-20 right-0 h-96 w-96 rounded-full bg-[#F26B2A]/20 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 -left-20 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-5xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-bold px-3 py-1.5 ring-1 ring-emerald-400/30">
              <BadgeCheck className="h-4 w-4" /> {c.vf_kicker}
            </span>
            <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1] lg:whitespace-nowrap">{c.vf_title}</h2>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-4xl">{c.vf_lead}</p>
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            {/* Column 1 — via app (required) */}
            <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }}
              data-testid="postad-vf-col1" className="rounded-3xl bg-white/[0.04] ring-1 ring-white/10 overflow-hidden flex flex-col">
              <div className="relative h-64 sm:h-72">
                <img src={IMG.viaApp} alt="Фото через приложение" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 text-white text-xs font-extrabold px-3 py-1.5 shadow">
                  <Smartphone className="h-3.5 w-3.5" /> {c.vf_col1_tag}
                </span>
                <div className="absolute bottom-4 left-4 flex flex-col items-start gap-2">
                  {BADGE_META.map(({ key, Icon, cls }) => (
                    <span key={key} className={`inline-flex items-center gap-2 rounded-full ${cls} text-white text-xs font-bold pl-2.5 pr-4 py-1.5 shadow-lg`}>
                      <Icon className="h-3.5 w-3.5" /> {c.vf_badges[key]}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-6 sm:p-7 flex-1">
                <h3 className="text-xl font-extrabold text-white">{c.vf_col1_title}</h3>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{c.vf_col1_sub}</p>
                <ul className="mt-4 space-y-2.5">
                  {c.vf_col1_points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-slate-200">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Column 2 — upload extras (optional) */}
            <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: 0.08 }}
              data-testid="postad-vf-col2" className="rounded-3xl bg-white/[0.04] ring-1 ring-white/10 overflow-hidden flex flex-col">
              <div className="relative h-64 sm:h-72">
                <img src={IMG.quality} alt="Обработанные фото" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 text-xs font-extrabold px-3 py-1.5 shadow">
                  <Camera className="h-3.5 w-3.5" /> {c.vf_col2_tag}
                </span>
              </div>
              <div className="p-6 sm:p-7 flex-1">
                <h3 className="text-xl font-extrabold text-white">{c.vf_col2_title}</h3>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{c.vf_col2_sub}</p>
                <ul className="mt-4 space-y-2.5">
                  {c.vf_col2_points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CheckCircle2 className="h-5 w-5 text-slate-300 shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-slate-200">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
          <div className="mt-12 flex justify-center">
            <AccountCTA testid="postad-verified-cta" className="bg-white text-[#0B1220] hover:bg-slate-100" />
          </div>
        </div>
      </section>

      {/* Online booking — emerald section */}
      <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-white border-b border-emerald-100" data-testid="postad-booking-section">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1.5">
              <CalendarCheck className="h-4 w-4" /> {c.bk_kicker}
            </span>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.08]">{c.bk_title}</h2>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">{c.bk_lead}</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {c.bk_points.map((p, i) => {
                const Icon = BK_ICONS[i];
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }} data-testid={`postad-bk-${i}`}
                    className="rounded-2xl bg-white border border-emerald-100 p-5 shadow-sm">
                    <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-emerald-600" />
                    </div>
                    <h4 className="mt-3 text-base font-bold text-slate-900">{p.t}</h4>
                    <p className="mt-1 text-sm text-slate-500 leading-relaxed">{p.d}</p>
                  </motion.div>
                );
              })}
            </div>
            <div className="mt-8">
              <AccountCTA testid="postad-booking-cta" className="bg-emerald-500 hover:bg-emerald-600 text-white" />
            </div>
          </div>

          <div className="space-y-5">
            {/* Schedule card */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              className="rounded-3xl border border-emerald-100 bg-white p-7 sm:p-8 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-emerald-500 flex items-center justify-center shadow">
                  <CalendarCheck className="h-6 w-6 text-white" />
                </div>
                <p className="text-base font-bold text-slate-900">{c.bk_schedule}</p>
              </div>
              <div className="mt-6 space-y-3">
                {[
                  { d: lang === "ru" ? "Пн" : "Mon", t: "09:00 – 11:00" },
                  { d: lang === "ru" ? "Чт" : "Thu", t: "09:00 – 11:00" },
                  { d: lang === "ru" ? "Пт" : "Fri", t: "14:00 – 16:30" },
                ].map((r, i) => (
                  <div key={i} className="flex items-center justify-between rounded-xl bg-emerald-50/60 border border-emerald-100 px-4 py-3">
                    <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
                      <span className="h-3.5 w-6 rounded-full bg-emerald-500 relative"><span className="absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full bg-white" /></span>
                      {r.d}
                    </span>
                    <span className="rounded-lg bg-white text-emerald-700 text-sm font-semibold px-3 py-1 border border-emerald-100">{r.t}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-2 text-xs font-semibold text-slate-500">
                <Clock className="h-4 w-4 text-emerald-500" /> {c.bk_hint}
              </div>
            </motion.div>

            {/* Questionnaire card */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
              data-testid="postad-questionnaire" className="rounded-3xl border border-slate-200 bg-white p-7 sm:p-8 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-[#7C3AED] flex items-center justify-center shadow">
                  <ClipboardList className="h-6 w-6 text-white" />
                </div>
                <p className="text-base font-bold text-slate-900">{c.bk_form_title}</p>
              </div>
              <div className="mt-6 space-y-2.5">
                {c.bk_form_fields.map((f, i) => {
                  const Icon = FORM_ICONS[i];
                  return (
                    <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3">
                      <Icon className="h-4 w-4 text-[#7C3AED] shrink-0" />
                      <span className="text-sm font-semibold text-slate-700">{f}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <AgentLinkSection lang={lang} />
      <TenantScreeningSection lang={lang} />

      {/* Telegram — blue section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-[#1C7FBF] to-[#229ED9] text-white" data-testid="postad-telegram-section">
        <div className="pointer-events-none absolute -top-24 -left-16 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 right-0 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white text-xs font-bold px-3 py-1.5 ring-1 ring-white/25">
              <Send className="h-4 w-4" /> {c.tg_kicker}
            </span>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08]">{c.tg_title}</h2>
            <p className="mt-6 text-base sm:text-lg text-white/90 max-w-2xl leading-relaxed">{c.tg_sub}</p>
            <div className="mt-8">
              <AccountCTA testid="postad-telegram-cta" className="bg-white text-[#1C7FBF] hover:bg-blue-50" />
            </div>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="rounded-3xl bg-white/10 ring-1 ring-white/20 backdrop-blur-md p-6 sm:p-7">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-white flex items-center justify-center shadow shrink-0">
                <Send className="h-6 w-6 text-[#229ED9]" />
              </div>
              <div>
                <p className="text-sm font-bold text-white">Tagdada Bot</p>
                <p className="text-xs text-white/70">@tagdada</p>
              </div>
            </div>
            <div className="mt-5 space-y-3">
              <div className="rounded-2xl rounded-tl-sm bg-white/90 text-slate-800 px-4 py-3 text-sm font-semibold max-w-[85%]">
                {lang === "ru" ? "🔔 Новая заявка на показ" : "🔔 New viewing request"}
                <p className="mt-1 text-xs font-medium text-slate-500">
                  {lang === "ru" ? "Завтра, 15:00 · 2-комн., ул. Тверская" : "Tomorrow, 3:00 PM · 2-room, Tverskaya st."}
                </p>
              </div>
              <div className="flex justify-end">
                <div className="rounded-2xl rounded-tr-sm bg-[#EAFBE7] text-emerald-800 px-4 py-2.5 text-sm font-bold">
                  {lang === "ru" ? "✅ Подтверждено" : "✅ Confirmed"}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Learn more about Tagdada */}
      <section className="bg-gradient-to-b from-white to-[#FFF9F5] border-t border-slate-100" data-testid="postad-learn-section">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#FFF1E8] text-[#E05A19] text-xs font-bold px-3 py-1.5">
              <Sparkles className="h-4 w-4" /> {c.learn_kicker}
            </span>
            <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 flex flex-wrap sm:flex-nowrap items-center justify-center gap-x-1.5">
              <img src={IMG.logo} alt="Tagdada" className="h-[1.2em] w-auto shrink-0" />
              <span className="sm:whitespace-nowrap">{c.learn_title}</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-3xl mx-auto">{c.learn_sub}</p>
          </div>

          {/* Contacts management */}
          <div className="mt-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }} data-testid="postad-contacts">
              <h3 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
                {c.cm_title_a}{" "}
                <span className="bg-gradient-to-r from-[#F26B2A] to-[#F59B42] bg-clip-text text-transparent">{c.cm_title_b}</span>
              </h3>
              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">{c.cm_sub}</p>
              <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-7">
                {c.cm_features.map((f, i) => {
                  const Icon = CM_ICONS[i];
                  return (
                    <div key={i} className="flex gap-3.5" data-testid={`postad-cm-feature-${i}`}>
                      <div className="h-11 w-11 shrink-0 rounded-2xl bg-[#FFF1E8] flex items-center justify-center">
                        <Icon className="h-5 w-5 text-[#F26B2A]" />
                      </div>
                      <div>
                        <h4 className="text-base font-extrabold text-slate-900">{f.t}</h4>
                        <p className="mt-1 text-sm text-slate-500 leading-relaxed">{f.d}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-9">
                <AccountCTA testid="postad-contacts-cta" className="bg-gradient-to-r from-[#F26B2A] to-[#F59B42] hover:from-[#E05A19] hover:to-[#E48A31] text-white" />
              </div>
            </motion.div>

            {/* mock contact card */}
            <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              className="rounded-3xl bg-gradient-to-br from-slate-50 to-white ring-1 ring-slate-100 p-6 sm:p-8 shadow-xl">
              <div className="rounded-3xl bg-white ring-1 ring-slate-100 shadow-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-2xl bg-[#F26B2A] flex items-center justify-center shadow">
                      <User className="h-6 w-6 text-white" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-2.5 w-32 rounded-full bg-slate-200" />
                      <div className="h-2.5 w-20 rounded-full bg-slate-100" />
                    </div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-orange-50 flex items-center justify-center">
                    <CheckCircle2 className="h-4 w-4 text-[#F26B2A]" />
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {[["bg-orange-100", "bg-orange-300"], ["bg-emerald-100", "bg-emerald-300"], ["bg-violet-100", "bg-violet-300"]].map(([bg, bar], i) => (
                    <span key={i} className={`inline-flex items-center gap-1.5 rounded-full ${bg} px-2.5 py-1.5`}>
                      <Tag className="h-3 w-3 text-slate-500" /><span className={`h-2 w-10 rounded-full ${bar}`} />
                    </span>
                  ))}
                </div>
                <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-3.5 flex items-start gap-2.5">
                  <MessageSquare className="h-4 w-4 text-[#F26B2A] mt-0.5 shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-2 w-full rounded-full bg-slate-200" />
                    <div className="h-2 w-2/3 rounded-full bg-slate-100" />
                  </div>
                </div>
                <div className="mt-4">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F26B2A] text-white text-sm font-bold px-4 py-2 shadow">
                    <Clock className="h-4 w-4" /> {lang === "ru" ? "Напомнить через 3 дня" : "Plan in 3 days"}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Meetings */}
          <div className="mt-24" data-testid="postad-meetings">
            <h3 className="text-center text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.12]">
              <span className="bg-gradient-to-r from-[#F26B2A] to-[#F59B42] bg-clip-text text-transparent">{c.mt_title_a}</span>{" "}
              <span className="text-slate-900">{c.mt_title_b}</span>
            </h3>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {c.mt_cards.map((m, i) => {
                const meta = MT_META[i];
                const Icon = meta.Icon;
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.07 }} data-testid={`postad-meeting-${i}`}
                    className="relative rounded-3xl bg-white border border-slate-200 shadow-sm hover:shadow-xl transition-all overflow-hidden flex flex-col">
                    <span className={`absolute top-0 inset-x-0 h-1.5 ${meta.bar}`} />
                    <div className="p-6 flex flex-col flex-1">
                      <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${meta.tile}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <h4 className="mt-4 text-lg font-extrabold text-slate-900">{m.t}</h4>
                      <p className="mt-2 text-sm text-slate-500 leading-relaxed flex-1">{m.d}</p>
                      <a href={BRAND.register} target="_blank" rel="noopener noreferrer" data-testid={`postad-meeting-cta-${i}`}
                        className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl text-white font-bold px-4 py-2.5 shadow-sm transition-all active:scale-[0.98] ${meta.btn}`}>
                        {c.mt_card_cta[i]} <ArrowRight className="h-4 w-4" />
                      </a>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <div className="mt-12 flex justify-center">
              <AccountCTA testid="postad-meetings-cta" className="bg-gradient-to-r from-[#F26B2A] to-[#F59B42] hover:from-[#E05A19] hover:to-[#E48A31] text-white" />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-6xl mx-auto rounded-3xl bg-gradient-to-r from-[#F26B2A] to-[#F59B42] px-8 py-14 sm:px-14 sm:py-16 text-center shadow-xl relative overflow-hidden">
          <div className="pointer-events-none absolute -top-16 -left-10 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white max-w-3xl mx-auto leading-tight">{c.final_title}</h2>
            <p className="mt-4 text-base sm:text-lg text-white/90">{c.final_sub}</p>
            <a href={BRAND.register} target="_blank" rel="noopener noreferrer" data-testid="postad-final-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-white text-[#E05A19] font-extrabold px-8 py-4 shadow-lg hover:bg-orange-50 transition-all active:scale-[0.98]">
              <Sparkles className="h-5 w-5" /> {c.final_cta} <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      <div className="flex-1" />
      <Footer />
    </div>
  );
}
